<template>
  <span class="tender-related-sources">
    <span v-for="source in sources" :key="source.id" class="tender-related-sources__source">
      <!-- In Aloha when the API found the tender and the user has its platform, else on its
           platform's site; plain text when the API found nothing. -->
      <BaseLink
        v-if="source.to || source.href"
        :to="source.to"
        :href="source.href"
        :target-blank="!!source.href"
        v-tooltip="{ content: source.tooltip, placement: 'top', offset: '8px' }"
        @click.native="onLinkClicked(source)"
      >
        <PlatformIcon :platform="source.platform" class="tender-related-sources__icon" />
        {{ source.name }}
      </BaseLink>
      <span v-else class="tender-related-sources__text">
        <PlatformIcon :platform="source.platform" class="tender-related-sources__icon" />
        {{ source.name }}
      </span>
      <span v-if="separated" class="tender-related-sources__separator"></span>
      <BaseTag tone="neutral" preset="soft">{{ source.number }}</BaseTag>
    </span>
  </span>
</template>

<script>
import BaseLink from '@/components/base/BaseLink';
import BaseTag from '@/components/base/BaseTag.vue';
import PlatformIcon from '@/components/PlatformIcon';
import PlatformName from '@/mixins/platform-name-mixin';

export default {
  name: 'TenderRelatedSources',
  components: {
    BaseLink,
    BaseTag,
    PlatformIcon,
  },
  mixins: [PlatformName],
  props: {
    relatedSources: {
      type: Array,
      required: true,
    },
    // A rule between the platform and its number, as the tender's own Provenance has.
    separated: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    sources() {
      return this.relatedSources.map((relatedSource) => ({
        id: relatedSource.id,
        platform: relatedSource.providerId,
        // A platform missing from the catalog still says which one it is.
        name: this.getPlatformName(relatedSource.providerId) || relatedSource.providerId,
        number: this.$t('tender-details.reference-num', { number: relatedSource.referenceNumber }),
        to: relatedSource.tenderId ? { name: 'tender-details', params: { id: relatedSource.tenderId } } : '',
        href: !relatedSource.tenderId && relatedSource.url ? relatedSource.url : '',
        tooltip: relatedSource.tenderId
          ? this.$t('tender-details.related-source-open')
          : this.$t('tender-details.origin-site-access'),
      }));
    },
  },
  methods: {
    onLinkClicked(source) {
      if (source.href) this.$track.tenderExternalLink(source.platform);
    },
  },
};
</script>

<style lang="scss">
.tender-related-sources {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;

  &__source {
    display: inline-flex;
    // Too narrow for the whole source, its number goes under its name, as Provenance's does.
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    // As tall as a link with its external-link icon, so the name lines up with one beside it
    // even when this link has no icon (in Aloha, or plain text).
    min-height: 24px;
  }

  &__text {
    display: inline-flex;
    align-items: center;
  }

  &__icon {
    padding-right: 8px;
  }

  &__separator {
    background: $color-border;
    height: 24px;
    width: 1px;
  }
}
</style>
